/**
 * Consumer Portal Application
 *
 * B2B2C self-service portal for consumers (end businesses of our business clients).
 * Uses centralized Auth Portal (Service 22) for authentication.
 */

import { GlassLoader } from '@ops-matrix/design-system-ui';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
// Layouts
import Layout from './components/layout/Layout';
import { useConsumerAuth } from './contexts/ConsumerAuthContext';
import { captureReferralMetadata, persistReferralToServer } from './lib/utils/referral-tracking';
// Public Loan Application (Issue #374 - Form-First Onboarding)
// Templated Loan Intake Wizard (Issue #432)
import { LoanApplicationPage, LoanIntakeWizard } from './pages/apply';
import CaseDetailPage from './pages/cases/CaseDetailPage';
import CaseListPage from './pages/cases/CaseListPage';
import ChatPage from './pages/chat/ChatPage';
// Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import DocumentsPage from './pages/documents/DocumentsPage';
import DocumentViewPage from './pages/documents/DocumentViewPage';
// Documents to sign (Issue #1308 — Term Sheet E-Signature MVP)
import DocumentsToSignPage from './pages/documents-to-sign/DocumentsToSignPage';
// Borrowing Entities & Guarantees (Issue #636)
import EntityHubPage from './pages/entities/EntityHubPage';
import MyEntitiesPage from './pages/entities/MyEntitiesPage';
// Guarantor Hub (Issue #1199)
import GuarantorHubPage from './pages/guarantors/GuarantorHubPage';
import LogoutPage from './pages/LogoutPage';
// Notification Pages (Issue #699)
import NotificationPreferencesPage from './pages/notifications/NotificationPreferencesPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import IntakeWizard from './pages/onboarding/IntakeWizard';
import ProfilePage from './pages/profile/ProfilePage';
// Proposal Pages (Issues #47, #51)
import ProposalDetailPage from './pages/proposals/ProposalDetailPage';
import ProposalsPage from './pages/proposals/ProposalsPage';
// Quotes Page (END_CONSUMER)
import QuotesPage from './pages/quotes/QuotesPage';
import EntityRecordsPage from './pages/records/EntityRecordsPage';
import RecordDetailPage from './pages/records/RecordDetailPage';
import RecordEditPage from './pages/records/RecordEditPage';
// Entity Record Pages
import RecordsPage from './pages/records/RecordsPage';
// Public E-Sign Page (Issue #56 - E-Sign Bridge)
import SignDocumentPage from './pages/sign/SignDocumentPage';
import SubmitRecordPage from './pages/submit/SubmitRecordPage';
// Watchlist (cross-portal port of Issue #1424)
import WatchlistPage from './pages/watchlist/WatchlistPage';

/**
 * Protected route wrapper
 * Redirects to Auth Portal if not authenticated
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, redirectToLogin } = useConsumerAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Get tenant from URL if present
      const tenantId = new URLSearchParams(window.location.search).get('tenant') || undefined;
      redirectToLogin(tenantId);
    }
  }, [isAuthenticated, isLoading, redirectToLogin]);

  if (isLoading || !isAuthenticated) {
    return <GlassLoader variant="fullPage" message="Loading..." />;
  }

  return <>{children}</>;
}

export default function App() {
  // Capture referral metadata on app load (UTM params, referral codes, etc.)
  // Issue #1223 PR3 GAP 13: persist to server on first capture.
  // Rehydration from server is handled by useReferralTracking() hook to avoid
  // a race condition where App grabs the sentinel before the hook can rehydrate.
  useEffect(() => {
    const captured = captureReferralMetadata();
    if (captured) {
      persistReferralToServer(captured);
    }
  }, []);

  return (
    <Routes>
      {/* Public routes - no auth required (Issue #374 - Form-First Onboarding) */}
      <Route path="/apply" element={<LoanApplicationPage />} />
      {/* Templated Loan Intake Wizard (Issue #432) */}
      <Route path="/apply/loan" element={<LoanIntakeWizard />} />
      {/* Public E-Sign route - borrowers may not have portal accounts (Issue #56) */}
      <Route path="/sign/:submitterId" element={<SignDocumentPage />} />
      {/* Logout route - public, no auth required */}
      <Route path="/logout" element={<LogoutPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="applications" element={<CaseListPage />} />
        <Route path="applications/new" element={<LoanIntakeWizard />} />
        <Route path="applications/:caseId" element={<CaseDetailPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="documents/:documentId/view" element={<DocumentViewPage />} />
        <Route path="documents/:documentId" element={<Navigate to="view" replace />} />
        {/* Documents to sign (Issue #1308) — feature-flagged route; internal
            <DocumentsToSignPage /> redirects to /dashboard when flag is off. */}
        <Route path="documents-to-sign" element={<DocumentsToSignPage />} />
        {/* AI Assistant Chat Page (Issue #809 - Feature 7) */}
        <Route path="chat" element={<ChatPage />} />
        {/* Notification Routes (Issue #699 - Notification System Fidelity) */}
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="notifications/preferences" element={<NotificationPreferencesPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Borrowing Entities & Guarantees (Issue #636) */}
        <Route path="entities/:entityId" element={<EntityHubPage />} />
        <Route path="entities" element={<MyEntitiesPage />} />
        {/* Guarantor Hub (Issue #1199) */}
        <Route path="guarantors" element={<GuarantorHubPage />} />
        {/* Entity Record Routes */}
        <Route path="records" element={<RecordsPage />} />
        <Route path="records/:entityId" element={<EntityRecordsPage />} />
        <Route path="records/:entityId/:recordId" element={<RecordDetailPage />} />
        <Route path="records/:entityId/:recordId/edit" element={<RecordEditPage />} />
        {/* Submit Routes */}
        <Route path="submit/:entityId" element={<SubmitRecordPage />} />
        {/* Onboarding / Intake */}
        <Route path="onboarding" element={<IntakeWizard />} />
        {/* Proposals Routes (Issues #47, #51) */}
        <Route path="proposals" element={<ProposalsPage />} />
        <Route path="proposals/:id" element={<ProposalDetailPage />} />
        {/* END_CONSUMER Routes */}
        <Route path="quotes" element={<QuotesPage />} />
        {/* Watchlist (cross-portal port of Issue #1424) */}
        <Route path="watchlist" element={<WatchlistPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
